/* ============================================================
   LA SILUETA — app.js V2
   Editorial luxe: magnetic cursor, scroll-driven, lightbox
   ============================================================ */

'use strict';

/* ── Scroll-reveal observer ─────────────────────────────── */
const revealObserver = new IntersectionObserver(
  (entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        revealObserver.unobserve(entry.target);
      }
    });
  },
  { threshold: 0.08, rootMargin: '0px 0px -50px 0px' }
);

document.querySelectorAll('.reveal, .split-line').forEach((el) => revealObserver.observe(el));

/* ── Sticky nav ─────────────────────────────────────────── */
const nav = document.getElementById('nav');
if (nav) {
  const navObserver = new IntersectionObserver(
    ([entry]) => {
      nav.classList.toggle('scrolled', !entry.isIntersecting);
    },
    { threshold: 0 }
  );
  const heroSentinel = document.getElementById('hero-sentinel');
  if (heroSentinel) navObserver.observe(heroSentinel);
  else if (window.scrollY > 80) nav.classList.add('scrolled');
  window.addEventListener('scroll', () => {
    if (!heroSentinel) nav.classList.toggle('scrolled', window.scrollY > 50);
  }, { passive: true });
}

/* ── Hamburger menu ─────────────────────────────────────── */
const hamburger = document.querySelector('.nav__hamburger');
const mobileNav = document.querySelector('.nav__mobile');

hamburger?.addEventListener('click', () => {
  const isOpen = hamburger.classList.toggle('is-open');
  mobileNav.classList.toggle('is-open', isOpen);
  document.body.style.overflow = isOpen ? 'hidden' : '';
});

mobileNav?.querySelectorAll('a').forEach((link) => {
  link.addEventListener('click', () => {
    hamburger.classList.remove('is-open');
    mobileNav.classList.remove('is-open');
    document.body.style.overflow = '';
  });
});

/* ── Service category tabs ──────────────────────────────── */
const serviceTabs = document.querySelectorAll('.service-tab');
const serviceCategories = document.querySelectorAll('.service-category');

serviceTabs.forEach((tab) => {
  tab.addEventListener('click', () => {
    const target = tab.dataset.cat;
    serviceTabs.forEach((t) => {
      t.classList.remove('is-active');
      t.setAttribute('aria-selected', 'false');
    });
    tab.classList.add('is-active');
    tab.setAttribute('aria-selected', 'true');

    serviceCategories.forEach((cat) => {
      if (target === 'all') {
        cat.classList.add('is-active');
      } else {
        cat.classList.toggle('is-active', cat.dataset.cat === target);
      }
    });
  });
});

/* ── Accordion ──────────────────────────────────────────── */
document.querySelectorAll('.accordion-trigger').forEach((trigger) => {
  trigger.addEventListener('click', () => {
    const item = trigger.closest('.accordion-item');
    const body = item.querySelector('.accordion-body');
    const isOpen = item.classList.contains('is-open');

    document.querySelectorAll('.accordion-item').forEach((i) => {
      i.classList.remove('is-open');
      const b = i.querySelector('.accordion-body');
      if (b) b.style.maxHeight = '0';
      const t = i.querySelector('.accordion-trigger');
      if (t) t.setAttribute('aria-expanded', 'false');
    });

    if (!isOpen) {
      item.classList.add('is-open');
      body.style.maxHeight = body.scrollHeight + 'px';
      trigger.setAttribute('aria-expanded', 'true');
    }
  });
});

/* ── Smooth scroll for anchor links ─────────────────────── */
document.querySelectorAll('a[href^="#"]').forEach((anchor) => {
  anchor.addEventListener('click', (e) => {
    const href = anchor.getAttribute('href');
    if (href === '#' || href.length < 2) return;
    const target = document.querySelector(href);
    if (target) {
      e.preventDefault();
      const offset = 80;
      const top = target.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  });
});

/* ── Active nav link on scroll ──────────────────────────── */
const sections = document.querySelectorAll('section[id]');
const navLinks = document.querySelectorAll('.nav__links a[href^="#"]');

if (sections.length && navLinks.length) {
  const sectionObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const id = entry.target.getAttribute('id');
          navLinks.forEach((link) => {
            link.classList.toggle(
              'is-active',
              link.getAttribute('href') === `#${id}`
            );
          });
        }
      });
    },
    { threshold: 0.35 }
  );
  sections.forEach((s) => sectionObserver.observe(s));
}

/* ── Magnetic cursor (desktop only) ─────────────────────── */
(function initCursor() {
  return; // disabled per Benjamin request 2026-05-05
  const dot = document.createElement('div');
  dot.className = 'cursor-dot';
  const ring = document.createElement('div');
  ring.className = 'cursor-ring';
  document.body.appendChild(dot);
  document.body.appendChild(ring);

  let mx = 0, my = 0;
  let rx = 0, ry = 0;

  document.addEventListener('mousemove', (e) => {
    mx = e.clientX;
    my = e.clientY;
    dot.style.transform = `translate(${mx}px, ${my}px) translate(-50%, -50%)`;
  }, { passive: true });

  function lerp() {
    rx += (mx - rx) * 0.18;
    ry += (my - ry) * 0.18;
    ring.style.transform = `translate(${rx}px, ${ry}px) translate(-50%, -50%)`;
    requestAnimationFrame(lerp);
  }
  lerp();

  // Hover interactive elements
  const hoverables = 'a, button, .service-card, .testimonial-card, .gallery-item, .accordion-trigger, .transfo-item';
  document.querySelectorAll(hoverables).forEach((el) => {
    el.addEventListener('mouseenter', () => {
      dot.classList.add('is-hover');
      ring.classList.add('is-hover');
    });
    el.addEventListener('mouseleave', () => {
      dot.classList.remove('is-hover');
      ring.classList.remove('is-hover');
    });
  });

  // Hide on leave window
  document.addEventListener('mouseleave', () => {
    dot.style.opacity = '0';
    ring.style.opacity = '0';
  });
  document.addEventListener('mouseenter', () => {
    dot.style.opacity = '1';
    ring.style.opacity = '1';
  });
})();

/* ── Magnetic CTAs ──────────────────────────────────────── */
(function magneticButtons() {
  if (window.matchMedia('(pointer: coarse)').matches) return;

  document.querySelectorAll('.btn--primary, .btn--outline').forEach((btn) => {
    btn.addEventListener('mousemove', (e) => {
      const rect = btn.getBoundingClientRect();
      const x = e.clientX - rect.left - rect.width / 2;
      const y = e.clientY - rect.top - rect.height / 2;
      btn.style.transform = `translate(${x * 0.18}px, ${y * 0.28}px)`;
    });
    btn.addEventListener('mouseleave', () => {
      btn.style.transform = '';
    });
  });
})();

/* ── Lightbox for transformation images ─────────────────── */
(function initLightbox() {
  const triggers = document.querySelectorAll('[data-lightbox]');
  if (!triggers.length) return;

  const overlay = document.createElement('div');
  overlay.className = 'lightbox';
  overlay.innerHTML = `
    <button class="lightbox__close" aria-label="Fermer">×</button>
    <img alt="" />
  `;
  document.body.appendChild(overlay);
  const img = overlay.querySelector('img');
  const closeBtn = overlay.querySelector('.lightbox__close');

  function close() {
    overlay.classList.remove('is-open');
    document.body.style.overflow = '';
    setTimeout(() => { img.src = ''; }, 400);
  }

  overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });
  closeBtn.addEventListener('click', close);
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && overlay.classList.contains('is-open')) close();
  });

  triggers.forEach((trig) => {
    trig.addEventListener('click', (e) => {
      e.preventDefault();
      const src = trig.dataset.lightbox || trig.querySelector('img')?.src;
      const alt = trig.querySelector('img')?.alt || '';
      if (!src) return;
      img.src = src;
      img.alt = alt;
      overlay.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    });
  });
})();

/* ── Preview mode helpers ───────────────────────────────── */
if (location.search.includes('preview=1')) {
  document.documentElement.dataset.preview = '1';
  document.querySelectorAll('.reveal, .split-line').forEach((el) => el.classList.add('is-visible'));
  // Open all accordions for screenshots
  document.querySelectorAll('.accordion-item').forEach((item) => {
    const body = item.querySelector('.accordion-body');
    if (body) body.style.maxHeight = body.scrollHeight + 'px';
    item.classList.add('is-open');
  });
}
